import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import EpisodeCard from "@/components/EpisodeCard";
import VideoModal from "@/components/VideoModal";
import { Calendar, Mic, Youtube, Music2, Podcast } from "lucide-react";

export default function ShowDetail() {
  const { slug } = useParams();
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get(`/shows/${slug}`).then((r) => setShow(r.data)).catch(() => setShow(false));
    api.get("/episodes", { params: { show: slug } }).then((r) => setEpisodes(r.data));
  }, [slug]);

  if (show === null) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-400">Loading…</div>;
  if (show === false) return <div className="max-w-7xl mx-auto px-6 py-24">Show not found. <Link to="/shows" className="text-gold underline">Back to shows.</Link></div>;

  const featured = episodes.find((e) => e.featured) || episodes[0];

  return (
    <div data-testid={`show-detail-${slug}`}>
      <VideoModal episode={playing} onClose={() => setPlaying(null)} />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="show-hero">
        <div className="absolute inset-0">
          <img src={show.artwork_url} alt="" className="w-full h-full object-cover opacity-30 blur-2xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="aspect-square overflow-hidden border border-white/10 shadow-2xl">
              <img src={show.artwork_url} alt={show.title} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: show.accent_color }}>
              {show.tagline}
            </p>
            <h1 className="mt-4 font-display text-5xl md:text-7xl font-black tracking-tighter leading-none">
              {show.title}
            </h1>
            <p className="mt-6 text-lg text-neutral-300 max-w-2xl font-body">{show.description}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {show.topics.map((t) => (
                <span key={t} className="text-xs uppercase tracking-widest text-white border border-white/15 px-3 py-1.5">
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-neutral-300">
              <div className="flex items-center gap-2"><Calendar size={16} className="text-gold" /> {show.schedule.join(", ")}</div>
              {show.hosts?.length > 0 && (
                <div className="flex items-center gap-2"><Mic size={16} className="text-gold" /> {show.hosts.join(", ")}</div>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {show.youtube_url && (
                <a href={show.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white/5">
                  <Youtube size={16} /> YouTube
                </a>
              )}
              {show.spotify_url && (
                <a href={show.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white/5">
                  <Music2 size={16} /> Spotify
                </a>
              )}
              {show.apple_podcasts_url && (
                <a href={show.apple_podcasts_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white/5">
                  <Podcast size={16} /> Apple Podcasts
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EPISODE */}
      {featured && (
        <section className="py-20 border-t border-white/5" data-testid="show-featured-episode">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-6">Featured Episode</p>
            <div className="grid lg:grid-cols-2 gap-10">
              <button onClick={() => setPlaying(featured)} className="block relative aspect-video bg-black border border-white/10 group">
                <img src={featured.thumbnail_url} alt={featured.title} className="w-full h-full object-cover group-hover:opacity-80 transition" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-svn-red text-white px-6 py-3 font-black uppercase tracking-widest text-sm">▶ Play</span>
                </div>
              </button>
              <div className="flex flex-col justify-center">
                <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight">{featured.title}</h3>
                <p className="mt-4 text-neutral-300 font-body">{featured.description}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* EPISODE LIBRARY */}
      <section className="py-20 border-t border-white/5 bg-[#080808]" data-testid="show-episodes">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mb-10">Episode Library</h2>
          {episodes.length === 0 ? (
            <p className="text-neutral-400">New episodes coming soon. Subscribe to be the first to hear.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((ep) => (
                <EpisodeCard key={ep.id} ep={ep} onPlay={setPlaying} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
